import { fetchAllPaginated } from "@/lib/supabase-paginate";
import type { ReportContext, ReportRow } from "@/lib/reports/types";

interface TaskDeviationInput {
  taskId: string;
  name: string;
  budsjett: number;
  brukt: number;
}

export function budsjettavvikTaskRows(data: TaskDeviationInput[]): ReportRow[] {
  return data.map((task) => ({
    id: task.taskId,
    label: task.name,
    canDrill: false,
    cells: {
      budsjett: task.budsjett,
      brukt: task.brukt,
      avvik: task.budsjett
        ? Math.round(((task.brukt - task.budsjett) / task.budsjett) * 100)
        : 0,
    },
  }));
}

export async function fetchTaskDeviations(ctx: ReportContext): Promise<TaskDeviationInput[]> {
  const projectId = ctx.drillPath.find((step) => step.grain === "prosjekt")?.id;
  if (!projectId) return [];

  const userId = ctx.filters.userId;
  const [budgetLines, timeEntries] = await Promise.all([
    fetchAllPaginated<{
      id: string;
      description: string | null;
      quantity_hours: number | null;
    }>(
      "budget_lines",
      "id, description, quantity_hours",
      (q) => q.eq("project_id", projectId).eq("cost_type", "internal"),
    ),
    fetchAllPaginated<{
      budget_line_id: string | null;
      duration_seconds: number | null;
      user_id: string;
    }>(
      "time_entries",
      "budget_line_id, duration_seconds, user_id",
      (q) => {
        const scoped = q.eq("project_id", projectId).is("deleted_at", null);
        return userId ? scoped.eq("user_id", userId) : scoped;
      },
    ),
  ]);

  let ownAssignments: Array<{ budget_line_id: string; hours: number | null }> = [];
  if (userId && budgetLines.length > 0) {
    ownAssignments = await fetchAllPaginated<{ budget_line_id: string; hours: number | null }>(
      "budget_line_assignments",
      "budget_line_id, hours",
      (q) => q.eq("user_id", userId).in("budget_line_id", budgetLines.map((line) => line.id)),
    );
  }

  const assignedByLine = new Map<string, number>();
  for (const assignment of ownAssignments) {
    assignedByLine.set(
      assignment.budget_line_id,
      (assignedByLine.get(assignment.budget_line_id) ?? 0) + Number(assignment.hours ?? 0),
    );
  }

  const usedByLine = new Map<string, number>();
  let unassignedHours = 0;
  for (const entry of timeEntries) {
    const hours = Number(entry.duration_seconds ?? 0) / 3600;
    if (!entry.budget_line_id) {
      unassignedHours += hours;
    } else {
      usedByLine.set(entry.budget_line_id, (usedByLine.get(entry.budget_line_id) ?? 0) + hours);
    }
  }

  const rows = budgetLines
    .map((line) => ({
      taskId: line.id,
      name: line.description?.trim() || "Oppgave uten navn",
      budsjett: userId ? assignedByLine.get(line.id) ?? 0 : Number(line.quantity_hours ?? 0),
      brukt: usedByLine.get(line.id) ?? 0,
    }))
    .filter((row) => !userId || row.budsjett > 0 || row.brukt > 0);

  if (unassignedHours > 0) {
    rows.push({
      taskId: `unassigned-${projectId}`,
      name: "Timer uten oppgavekobling",
      budsjett: 0,
      brukt: unassignedHours,
    });
  }

  return rows.map((row) => ({
    ...row,
    budsjett: Math.round(row.budsjett * 10) / 10,
    brukt: Math.round(row.brukt * 10) / 10,
  }));
}
